/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useBooking } from "../useBooking";
import {
  useInvoicePromotionDeleteMutation,
  useInvoicePromotionPostMutation,
} from "../../lib/react-query/useInvoicePromotion";
import useInvoice from "./useInvoice";

export const usePromotionCode = ({ disabled = false } = {}) => {
  const { promotionCode, clearPromotionCode } = useBooking();
  const { invoice, setInvoice } = useInvoice();

  const [isApplying, setIsApplying] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const autoApplyAttemptedRef = useRef(false);

  const appliedPromotionCodes = invoice.promotion_instances || [];
  const hasExistingPromotion = appliedPromotionCodes.length > 0;

  const { mutateAsync } = useInvoicePromotionPostMutation(invoice.id);
  const { mutateAsync: mutateDelete } = useInvoicePromotionDeleteMutation(invoice.id);

  const applyCode = useCallback(
    async (code, { onSuccess, onError } = {}) => {
      setError(null);
      setIsApplying(true);

      try {
        const response = await mutateAsync({ code });

        if (typeof response === "string" || response?.error) {
          const errorMsg = response?.error || response;

          setError(errorMsg);
          onError?.(errorMsg);

          return;
        }

        setInvoice(response);
        clearPromotionCode();
        onSuccess?.(response, code);
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.response?.data || err.message;

        setError(errorMsg);
        onError?.(errorMsg);
      } finally {
        setIsApplying(false);
      }
    },
    [mutateAsync, setInvoice, clearPromotionCode]
  );

  const deleteCode = useCallback(
    async (instanceId, { onSuccess, onError } = {}) => {
      setError(null);
      setDeletingId(instanceId);

      try {
        const response = await mutateDelete(instanceId);

        if (typeof response === "string" || response?.error) {
          const errorMsg = response?.error || response;

          setError(errorMsg);
          onError?.(errorMsg);

          return;
        }

        setInvoice(response);
        onSuccess?.(response);
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.response?.data || err.message;

        setError(errorMsg);
        onError?.(errorMsg);
      } finally {
        setDeletingId(null);
      }
    },
    [mutateDelete, setInvoice]
  );

  const reset = useCallback(() => {
    setError(null);
    clearPromotionCode();
  }, [clearPromotionCode]);

  // Auto-apply promotion code from URL params (runs once per mount)
  useEffect(() => {
    const shouldAutoApply = promotionCode && !autoApplyAttemptedRef.current && !hasExistingPromotion && !disabled;

    if (shouldAutoApply) {
      autoApplyAttemptedRef.current = true;
      applyCode(promotionCode);
    }
  }, [promotionCode, hasExistingPromotion, disabled, applyCode]);

  return {
    isApplying,
    deletingId,
    error,
    appliedPromotionCodes,
    appliedCount: appliedPromotionCodes.length,
    hasExistingPromotion,
    promotionCode,
    applyCode,
    deleteCode,
    reset,
  };
};
